import {addMonths, startOfMonth, subMonths} from "date-fns"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import {theme} from "@/theme"
import {DateRange} from "@/DateRangeSelector"
import {MetricHistory} from "@/cube/metric-data"
import {MemberSnapshotDimension, Metric} from "@/cube"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {toKey} from "@/cube/toKey"
import * as _ from "lodash"
import {formatValue} from "@/utils/formatter"
import {LineSeriesDataItem} from "@amcharts/amcharts4/charts"

export class SparklineChart {
  private readonly chart: am4charts.XYChart
  private readonly selectedRangeSeries: am4charts.DateAxisDataItem

  constructor(
    readonly el: HTMLElement,
    readonly metricHistory: MetricHistory,
    onDateRangeSelected: (dateRange: DateRange) => void
  ) {
    this.chart = this.sparklineChart(el, metricHistory, onDateRangeSelected)
    this.selectedRangeSeries = (<am4charts.DateAxis>(
      this.chart.xAxes.getIndex(0)
    )).axisRanges.getIndex(0)!

    // Only handle chart events, preventing ripple or any parent widget action
    el.addEventListener("click", ev => ev.stopPropagation())
  }

  updateData(metricData: MetricHistory): void {
    if (this.chart.data.length === 0) {
      this.chart.data = <Array<any>>metricData.timeSeries
      this.chart.invalidateData()
    } else {
      keyedDataUpdate(
        metricData.metric,
        (row: any) => toKey(row, [MemberSnapshotDimension._atMonth]),
        metricData.timeSeries!,
        this.chart.data
      )
      this.chart.invalidateRawData()
    }
  }

  highlightSelectedRange(selectedDateRange: DateRange): void {
    this.selectedRangeSeries.date = selectedDateRange.start
    // Need to add one month so it includes the last item as well
    this.selectedRangeSeries.endDate = addMonths(selectedDateRange.end, 1)
  }

  dispose(): void {
    this.chart.dispose()
  }

  private sparklineChart(
    el: HTMLElement,
    metricHistory: MetricHistory,
    onDateRangeSelected: (dateRange: DateRange) => void
  ) {
    console.log("CREATING sparkline for", metricHistory)
    const container = am4core.create(el, am4core.Container)
    container.layout = "grid"
    container.fixedWidthGrid = false
    container.width = am4core.percent(100)
    container.height = am4core.percent(90)

    const chart = container.createChild(am4charts.XYChart)
    chart.tapToActivate = true
    chart.data = metricHistory.timeSeries
    chart.padding(5, 5, 2, 0)

    chart.dateFormatter.dateFormat = "MMM yyyy"
    chart.numberFormatter.numberFormat = "#,###."

    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.renderer.grid.template.disabled = true
    dateAxis.renderer.labels.template.disabled = true
    dateAxis.startLocation = 0.25
    dateAxis.endLocation = 0.7
    dateAxis.cursorTooltipEnabled = false

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    valueAxis.renderer.grid.template.disabled = true
    valueAxis.renderer.baseGrid.disabled = true
    valueAxis.renderer.labels.template.disabled = true
    valueAxis.cursorTooltipEnabled = false
    valueAxis.renderer.minGridDistance = 18
    // Multiply by 5% to give series a small buffer so it doesn't get cut off
    if (!_.isEmpty(metricHistory.timeSeries)) {
      valueAxis.max =
        _.maxBy<{[key in Metric]: number}>(
          metricHistory.timeSeries,
          this.metricHistory.metric
        )![metricHistory.metric] * 1.05
    }
    const series = chart.series.push(new am4charts.LineSeries())
    series.tooltipText = `{dateX} \n[bold]{valueY}`
    series.dataFields.dateX = MemberSnapshotDimension._atMonth
    series.dataFields.valueY = metricHistory.metric
    series.fillOpacity = 0.1
    series.fill = am4core.color("#d7d7d7")

    series.adapter.add("tooltipText", (value, target) => {
      const valueY = formatValue(
        this.metricHistory.metric,
        (<LineSeriesDataItem>target.tooltipDataItem).valueY
      )
      return `{dateX} \n[bold]${valueY}`
    })

    series.tensionX = 0.8
    series.strokeWidth = 2

    // FIXME use theme colour
    series.stroke = am4core.color("#d7d7d7")

    const selectedRangeSeries = dateAxis.createSeriesRange(series)
    selectedRangeSeries.contents.stroke = chart.colors.getIndex(0)
    selectedRangeSeries.contents.strokeOpacity = 1
    selectedRangeSeries.contents.strokeWidth = 3
    selectedRangeSeries.contents.fill = am4core.color(theme().primaryColor)
    selectedRangeSeries.contents.fillOpacity = 0.15

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.lineY.disabled = true
    chart.cursor.behavior = "none"
    chart.zoomOutButton.disabled = true

    chart.cursor.events.on("zoomstarted", ev => {
      const start = startOfMonth(dateAxis.positionToDate(ev.target.xPosition))
      const end = subMonths(startOfMonth(this.selectedRangeSeries.endDate), 1)

      onDateRangeSelected({start, end})
    })

    return chart
  }
}
